import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import HeroSection from '../components/HeroSection';
import { Link } from 'react-router-dom';
import { Heart, Shield, Award, Users, CheckCircle, Church } from 'lucide-react';
import { ChristianIcon, ScriptureQuote } from '../components/ChristianElements';

const Home: React.FC = () => {
  const { t } = useTranslation();

  const trustBadges = [
    {
      icon: Heart,
      title: t('trust.handcrafted.title'),
      description: t('trust.handcrafted.description'),
      christianIcon: 'cross'
    },
    {
      icon: Shield,
      title: t('trust.sacredMaterials.title'),
      description: t('trust.sacredMaterials.description'),
      christianIcon: 'olive'
    },
    {
      icon: Award,
      title: t('trust.masterCraftsmanship.title'),
      description: t('trust.masterCraftsmanship.description'),
      christianIcon: 'vine'
    },
    {
      icon: Users,
      title: t('trust.familyTrusted.title'),
      description: t('trust.familyTrusted.description'),
      christianIcon: 'dove'
    },
    {
      icon: CheckCircle,
      title: t('trust.churchCertification.title'),
      description: t('trust.churchCertification.description'),
      christianIcon: 'cross'
    },
    {
      icon: Church,
      title: t('trust.supportingChurches.title'),
      description: t('trust.supportingChurches.description'),
      christianIcon: 'cross'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('meta.home.title')}</title>
        <meta 
          name="description" 
          content={t('meta.home.description')} 
        />
        <meta name="keywords" content={t('meta.home.keywords')} />
        <link rel="canonical" href={t('meta.home.canonical')} />
        
        {/* Open Graph */}
        <meta property="og:title" content={t('meta.home.ogTitle')} />
        <meta property="og:description" content={t('meta.home.ogDescription')} />
        <meta property="og:type" content={t('meta.home.ogType')} />
        <meta property="og:url" content={t('meta.home.ogUrl')} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": t('structuredData.organization.context'),
            "@type": t('structuredData.organization.type'),
            "name": t('meta.home.structuredData.organizationName'),
            "description": t('meta.home.structuredData.organizationDescription'),
            "url": t('meta.home.structuredData.organizationUrl'),
            "address": {
              "@type": "PostalAddress",
              "addressLocality": t('meta.home.structuredData.addressLocality'),
              "addressCountry": t('meta.home.structuredData.addressCountry')
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": t('meta.home.structuredData.telephone'),
              "contactType": t('meta.home.structuredData.contactType'),
              "email": t('meta.home.structuredData.email')
            }
          })}
        </script>
      </Helmet>

      <HeroSection />

      {/* Intro Section */}
      <section id="intro-section" className="section-padding bg-white sacred-border christian-gradient">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center px-4">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="font-scripture text-2xl md:text-3xl font-bold text-primary-800 mb-2">
                {t('story.title')}
              </h2>
              <div className="w-16 md:w-24 h-1 bg-gold-500 mx-auto"></div>
            </div>
            <div className="mb-6 md:mb-8">
              <ChristianIcon icon="cross" size="lg" className="mx-auto mb-4" />
            </div>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed px-2">
              {t('story.content')}
            </p>
            
            {/* Scripture quote */}
            <div className="mb-6 md:mb-8">
              <ScriptureQuote 
                verse={t('scriptures.peace.verse')}
                reference={t('scriptures.peace.reference')}
                className="max-w-2xl mx-auto"
              />
            </div>
            
            <Link
              to="/story"
              className="btn-primary inline-flex items-center text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
            >
              <ChristianIcon icon="dove" size="sm" className="mr-2" />
              {t('common.readMore')}
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="section-padding bg-primary-50 sacred-border christian-gradient">
        <div className="container-max">
          <div className="text-center mb-6 md:mb-8">
            <h2 className="font-scripture text-2xl md:text-3xl font-bold text-primary-800 mb-2">
              {t('trust.title')}
            </h2>
            <div className="w-16 md:w-24 h-1 bg-gold-500 mx-auto"></div>
          </div>
          <div className="text-center mb-8 md:mb-12">
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('trust.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 lg:flex lg:flex-wrap lg:justify-center lg:gap-8">
            {trustBadges.map((badge, index) => (
              <div
                key={index}
                className="text-center sacred-border p-4 md:p-6 bg-white/50 w-full sm:w-full lg:w-1/3 lg:max-w-[300px] min-h-[200px] flex flex-col"
              >
                <div className="relative mb-3 md:mb-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto">
                    <badge.icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                    <ChristianIcon icon={badge.christianIcon as any} size="sm" />
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-scripture font-semibold text-primary-800 mb-2">
                  {badge.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 px-2 flex-1">
                  {badge.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Craft Process Preview */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-2 md:mb-4">
              {t('craft.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('craft.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-olive-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <span className="text-lg md:text-2xl font-bold text-olive-600">1</span>
              </div>
              <h3 className="text-lg md:text-xl font-serif font-semibold text-primary-800 mb-2">
                {t('craft.process.design.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600 px-2">
                {t('craft.process.design.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-olive-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <span className="text-lg md:text-2xl font-bold text-olive-600">2</span>
              </div>
              <h3 className="text-lg md:text-xl font-serif font-semibold text-primary-800 mb-2">
                {t('craft.process.craft.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600 px-2">
                {t('craft.process.craft.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-olive-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <span className="text-lg md:text-2xl font-bold text-olive-600">3</span>
              </div>
              <h3 className="text-lg md:text-xl font-serif font-semibold text-primary-800 mb-2">
                {t('craft.process.soil.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600 px-2">
                {t('craft.process.soil.description')}
              </p>
            </div>
          </div>

          <div className="text-center mt-6 md:mt-8">
            <Link
              to="/craft"
              className="btn-secondary inline-flex items-center text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
            >
              {t('common.readMore')}
            </Link>
          </div>
        </div>
      </section>

      {/* Materials Preview */}
      {/* <section className="section-padding bg-primary-50">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-4 md:mb-6">
                {t('materials.title')}
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8">
                {t('materials.subtitle')}
              </p>
              
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h3 className="text-lg md:text-xl font-serif font-semibold text-primary-800 mb-2">
                    {t('materials.soil.title')}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    {t('materials.soil.description')}
                  </p>
                </div>
              </div>

              <div className="mt-6 md:mt-8">
                <Link
                  to="/materials"
                  className="btn-primary inline-flex items-center text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
                >
                  {t('common.readMore')}
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6 lg:mt-0">
              <img 
                src="/images/coffin1.jpg" 
                alt="Handcrafted wooden coffin from Nazareth"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className="section-padding bg-primary-800 text-white holy-land-bg">
        <div className="container-max text-center">
          <div className="mb-4 md:mb-6">
            <ChristianIcon icon="cross" size="lg" className="mx-auto text-gold-400" />
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-scripture font-bold mb-4 md:mb-6">
            {t('cta.honorTitle')}
          </h2>
          <p className="text-base md:text-xl text-primary-100 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            {t('cta.honorSubtitle')}
          </p>
          
          {/* Scripture quote */}
          <div className="mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            <ScriptureQuote 
             className="bg-white/10 backdrop-blur-sm border-gold-400/50 text-white"
              verse={t('scriptures.eternal.verse')}
              reference={t('scriptures.eternal.reference')}
             
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            <Link
              to="/contact"
              className="bg-white text-primary-800 hover:bg-primary-50 font-medium py-3 md:py-4 px-6 md:px-8 rounded-lg transition-colors duration-200 inline-flex items-center justify-center shadow-lg text-base md:text-lg"
            >
              <ChristianIcon icon="dove" size="sm" className="mr-2" />
              {t('cta.requestQuote')}
            </Link>
            
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
